import axios from 'axios';
import config from '../config';
import { messageErreur } from '../utils/messageErreur';

/**
 * Les accès d'une carte client : la seule façon d'appeler ces routes.
 *
 * ## Pourquoi ce module existe
 *
 * Le même geste — rattacher une adresse à une carte, la détacher, relancer son invitation — se
 * fait depuis **trois** écrans : l'écran Clients, la modale du Gestionnaire, et la fiche d'un
 * accès client. Les deux premiers avaient déjà divergé : l'un lit la réponse du rattachement en
 * `response.data?.access || response.data`, l'autre non, parce que la route rend un **tableau
 * nu** là où on l'attendait enveloppé.
 *
 * Tout ce qui peut diverger vit donc ici : l'URL, la forme de la réponse, la sémantique
 * d'`isActive`, et le statut qui autorise une relance. ⚠️ **Ce qui n'est PAS partagé, c'est la
 * vue** : les deux écrans existants listent *les adresses d'une carte*, la fiche liste *les
 * cartes d'une adresse*. C'est la matrice transposée ; un composant servant les deux sens serait
 * deux composants dans un fichier.
 *
 * ⚠️ **Les erreurs passent par `messageErreur`.** Ces routes répondent `{ error: '…' }`, mais
 * une passerelle répond `{ code, message }` : un objet atteignant le rendu démonte l'arbre React
 * et efface du même coup le message qui l'expliquait.
 *
 * @module api/accesCartes
 */

const BASE = `${config.API_URL}/api/client-management`;

const entetes = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

/**
 * Déballe une réponse qui peut être un tableau nu, un objet nu, ou enveloppé.
 *
 * ⚠️ C'est la divergence constatée entre les deux écrans existants : `POST …/access` rend la
 * ligne créée directement, et un appelant qui cherchait `data.access` recevait `undefined` sans
 * qu'aucune erreur ne soit levée — l'écriture avait réussi, et l'écran ne le montrait pas.
 */
const deballer = (donnees) => {
  if (Array.isArray(donnees)) return donnees;
  if (donnees && typeof donnees === 'object') return donnees.access ?? donnees.data ?? donnees;
  return donnees;
};

/** Relance l'erreur avec une phrase, jamais avec l'objet reçu. */
const echouer = (erreur, repli) => { throw new Error(messageErreur(erreur, repli)); };

/** Les cartes clients que l'appelant peut administrer. */
export async function listerCartes(token) {
  try {
    const reponse = await axios.get(`${BASE}/clients`, entetes(token));
    return deballer(reponse.data) || [];
  } catch (erreur) {
    return echouer(erreur, "La liste des clients n'a pas pu être chargée.");
  }
}

/** Les adresses rattachées à une carte, avec l'état de leur invitation. */
export async function lireAccesDUneCarte(token, clientId) {
  try {
    const reponse = await axios.get(`${BASE}/clients/${clientId}/access`, entetes(token));
    return deballer(reponse.data) || [];
  } catch (erreur) {
    return echouer(erreur, "Les accès de ce client n'ont pas pu être chargés.");
  }
}

/**
 * Rattache une adresse à une carte.
 *
 * ⚠️ **Ce geste envoie un lien de connexion.** C'est cette route, et elle seule, qui crée
 * l'invitation et le jeton à usage unique : court-circuiter l'appel pour écrire un `ClientEmail`
 * directement produirait un rattachement muet, dont le client n'entendrait jamais parler.
 */
export async function rattacherEmail(token, clientId, email) {
  try {
    const reponse = await axios.post(
      `${BASE}/clients/${clientId}/access`, { email }, entetes(token));
    return deballer(reponse.data);
  } catch (erreur) {
    return echouer(erreur, "Cette adresse n'a pas pu être rattachée.");
  }
}

/** Retire une adresse d'une carte. Le compte et les autres rattachements sont intacts. */
export async function detacherEmail(token, clientId, accessId) {
  try {
    const reponse = await axios.delete(
      `${BASE}/clients/${clientId}/access/${accessId}`, entetes(token));
    return deballer(reponse.data);
  } catch (erreur) {
    return echouer(erreur, "Cette adresse n'a pas pu être détachée.");
  }
}

/**
 * Suspend ou rétablit un accès, sans défaire le rattachement.
 *
 * `isActive: false` est le geste **réversible** : le périmètre du client se ferme — les lectures
 * ne retiennent que les rattachements actifs — et la ligne reste, avec son invitation. Détacher,
 * à l'inverse, supprime le rattachement et son invitation : un nouveau lien devra partir.
 */
export async function changerActivite(token, clientId, accessId, isActive) {
  try {
    const reponse = await axios.put(
      `${BASE}/clients/${clientId}/access/${accessId}`, { isActive }, entetes(token));
    return deballer(reponse.data);
  } catch (erreur) {
    return echouer(erreur, "L'état de cet accès n'a pas pu être changé.");
  }
}

/**
 * Renvoie le lien de connexion.
 *
 * ⚠️ N'a de sens que sur une invitation en attente ou expirée — `relanceUtile` le dit, et les
 * trois écrans doivent s'y fier plutôt que de recopier la liste des statuts : offrir la relance
 * sur une invitation acceptée enverrait un second lien à quelqu'un qui est déjà connecté.
 */
export async function relancerInvitation(token, clientId, accessId) {
  try {
    const reponse = await axios.post(
      `${BASE}/clients/${clientId}/access/${accessId}/resend-invitation`, {}, entetes(token));
    return deballer(reponse.data);
  } catch (erreur) {
    return echouer(erreur, "L'invitation n'a pas pu être renvoyée.");
  }
}

/** Les deux seuls statuts où renvoyer un lien a du sens. */
export const relanceUtile = (statut) => ['PENDING', 'EXPIRED'].includes(statut);
